/** Texto claro e escuro usados sobre o fundo configurado no CMS. */
export const TEXTO_CLARO = '#FFFFFF';
export const TEXTO_ESCURO = '#282828';

const luminanciaDe = (hex: string): number => {
  const canais = [0, 2, 4].map(i => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * canais[0] + 0.7152 * canais[1] + 0.0722 * canais[2];
};

const razaoDeContraste = (luminanciaA: number, luminanciaB: number): number =>
  (Math.max(luminanciaA, luminanciaB) + 0.05) /
  (Math.min(luminanciaA, luminanciaB) + 0.05);

/**
 * Escolhe entre texto claro e escuro conforme a luminancia do fundo, para o
 * texto nunca ficar ilegivel sobre a cor que o operador escolheu.
 *
 * Usa a luminancia relativa da WCAG (com a correcao de gamma), e nao a media
 * simples dos canais: o olho enxerga verde muito mais que azul, entao
 * #00FF00 e #0000FF tem brilho percebido bem diferente apesar de valores
 * numericos iguais.
 *
 * Em vez de um limiar fixo, compara a razao de contraste do fundo com cada
 * uma das duas cores de texto e fica com a maior. Assim o resultado vale para
 * o cinza escuro real (#282828), e nao para um preto puro hipotetico.
 */
export default function textColorForBackground(fundo?: string | null): string {
  const hex = (fundo ?? '').trim().replace('#', '');

  const normalizado =
    hex.length === 3
      ? hex
          .split('')
          .map(c => c + c)
          .join('')
      : hex;

  if (!/^[0-9a-fA-F]{6}$/.test(normalizado)) return TEXTO_ESCURO;

  const luminanciaFundo = luminanciaDe(normalizado);
  const luminanciaTextoClaro = luminanciaDe(TEXTO_CLARO.slice(1));
  const luminanciaTextoEscuro = luminanciaDe(TEXTO_ESCURO.slice(1));

  return razaoDeContraste(luminanciaFundo, luminanciaTextoClaro) >
    razaoDeContraste(luminanciaFundo, luminanciaTextoEscuro)
    ? TEXTO_CLARO
    : TEXTO_ESCURO;
}
